import type { Theme } from '@mui/material/styles';
import base from './base';
import palette from './palette';

const sharedIconButtonStates = {
  '&:hover': {
    backgroundColor: palette.primary.light,
  },
  '&:hover:active': {
    backgroundColor: palette.primary.dark,
  },
  '&:focus-visible': {
    backgroundColor: palette.primary.light,
    borderColor: palette.primary.dark,
    color: palette.common.white,
    outline: '1px solid ' + palette.primary.dark,
    outlineOffset: '-2px',
  },
  '&:disabled': {
    backgroundColor: palette.grey[100],
    borderColor: palette.grey[600],
    color: palette.text.secondary,
  },
};

const button = {
  MuiButtonBase: {
    defaultProps: {
      disableRipple: true,
    },
  },
  MuiButton: {
    defaultProps: {
      disableElevation: true,
    },
    styleOverrides: {
      root: {
        textTransform: 'none',
      },
      sizeMedium: {
        fontSize: '1rem',
      },
      sizeSmall: {
        fontSize: '0.875rem',
      },
      contained: {
        border: '1px solid transparent',
        padding: '5px 15px',
      },
      containedPrimary: {
        '&:hover': {
          backgroundColor: palette.primary.light,
        },
        '&:hover:active': {
          backgroundColor: palette.primary.dark,
        },
        '&:focus-visible': {
          backgroundColor: palette.primary.light,
          borderColor: palette.primary.dark,
          outline: `1px solid ${palette.primary.dark}`,
          outlineOffset: '-2px',
        },
        '&:disabled': {
          backgroundColor: palette.grey[100],
          borderColor: palette.grey[600],
          color: palette.text.secondary,
        },
      },
      outlined: ({ theme }: { theme: Theme }) => ({
        transition: theme.transitions.create(
          ['background-color', 'border-color', 'color'],
          { duration: theme.transitions.duration.short },
        ),
      }),
      outlinedPrimary: {
        borderColor: palette.primary.main,
        '&:hover': {
          backgroundColor: palette.primary.light,
          borderColor: palette.primary.light,
          color: palette.common.white,
        },
        '&:hover:active': {
          backgroundColor: palette.primary.dark,
          color: palette.common.white,
        },
        '&:focus-visible': {
          backgroundColor: palette.primary.light,
          borderColor: palette.primary.dark,
          color: palette.common.white,
          outline: `1px solid ${palette.primary.dark}`,
          outlineOffset: '-2px',
        },
        '&:disabled': {
          backgroundColor: palette.grey[100],
          borderColor: palette.grey[600],
          color: palette.text.secondary,
        },
      },
      outlinedSecondary: {
        borderColor: palette.grey[600],
        color: palette.grey[800],
          ...sharedIconButtonStates,
        '&:hover, &:hover:active': {
          borderColor: 'transparent',
          color: palette.common.white,
        },
      },
      text: {
        lineHeight: 1.2,
        borderRadius: base.shape.borderRadius,
        padding: '2px',
        '&:hover': {
          backgroundColor: 'transparent',
          color: palette.primary.light,
          textDecoration: 'underline',
        },
        '&:hover:active': {
          color: palette.primary.dark,
        },
        '&:focus-visible': {
          backgroundColor: palette.red[100],
          outline: '1px dashed' + palette.primary.main,
          outlineOffset: 0,
        },
        '&:disabled': {
          color: palette.grey[700],
        },
      },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        border: '1px solid transparent',
        borderRadius: base.shape.borderRadius,
        '.MuiSvgIcon-root': {
          transition: 'color 150ms cubic-bezier(0.4, 0, 0.2, 1)',
        },
        '&:hover': {
          backgroundColor: palette.grey[600],
          color: palette.common.white,
        },
        '&:hover:active': {
          backgroundColor: palette.grey[800],
          color: palette.common.white,
        },
        '&:focus-visible': {
          backgroundColor: palette.grey[600],
          borderColor: palette.grey[800],
          color: palette.common.white,
          outline: '1px solid ' + palette.grey[800],
          outlineOffset: '-1px',
        },
        '&:disabled': {
          color: palette.grey[600],
        },
      },
      colorPrimary: ({ theme }: { theme: Theme }) => ({
        backgroundColor: palette.primary.main,
        color: palette.common.white,
        transition: theme.transitions.create(
          ['background-color', 'border-color', 'color'],
          { duration: theme.transitions.duration.short },
        ),
        ...sharedIconButtonStates,
      }),
      colorSecondary: ({ theme }: { theme: Theme }) => ({
        color: palette.primary.main,
        borderColor: palette.grey[600],
        transition: theme.transitions.create(
          ['background-color', 'border-color', 'color'],
          { duration: theme.transitions.duration.short },
        ),
        ...sharedIconButtonStates,
        '&:hover, &:hover:active': {
          borderColor: 'transparent',
        },
      }),
      colorViolet: {
        borderColor: palette.blue[900],
        color: palette.blue[900],
        '& .MuiSvgIcon-root': {
          color: palette.blue[900],
        },
        '&:hover, &:hover:active': {
          backgroundColor: palette.blue[900],
          color: palette.common.white,
          '& .MuiSvgIcon-root': {
            color: palette.common.white,
          },
        },
        '&:focus-visible': {
          backgroundColor: palette.blue[100],
          borderColor: palette.blue[900],
          color: palette.blue[900],
          outline: '1px solid ' + palette.blue[900],
          outlineOffset: '-2px',
          '& .MuiSvgIcon-root': {
            color: 'inherit',
          },
        },
      },
      sizeMedium: {
        padding: 7,
      },
      sizeSmall: {
        padding: 1,
      },
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: {
        border: 'none',
        color: palette.grey[800],
        fontSize: '1rem',
        padding: 3,
        textTransform: 'none',
        '&.Mui-selected, &:hover': {
          color: palette.common.white,
          borderRadius: '3px',
        },
        '&:hover': {
          backgroundColor: palette.primary.light,
        },
        '&.Mui-selected, &.Mui-selected:hover': {
          backgroundColor: palette.primary.main,
        },
        '&:focus-visible': {
          backgroundColor: palette.primary.light,
          borderColor: palette.primary.dark,
          color: palette.common.white,
          outline: '1px solid ' + palette.primary.dark,
          outlineOffset: '-2px',
        },
      },
    },
  },
  MuiToggleButtonGroup: {
    styleOverrides: {
      root: {
        backgroundColor: palette.grey[100],
        border: '1px solid',
        borderColor: palette.grey[600],
        padding: 2,
        '&:has(.MuiToggleButton-root:not(.Mui-selected):hover) .MuiToggleButton-root.Mui-selected:not(:hover)': {
          backgroundColor: 'transparent',
          color: palette.primary.main,
        },
      },
    },
  },
};

export default button;
